/**
 * Organization Setting Service
 * API client for organization settings management
 */

import api from '@/lib/axios'
import { ORGANIZATION_API_ENDPOINTS } from '@/config/api/organization.api.constants'
import { tokenManager } from '@/utils/tokenManager'
import { decodeJWT } from '@/utils/permissionUtils'
import type {
  Setting,
  SettingCategory,
  BulkUpdateSettingsInput,
  GetSettingsParams,
} from '@/types/platform/setting.types'

class OrganizationSettingService {
  /**
   * Get organization slug from context token
   */
  private getOrgSlug(): string {
    const contextToken = tokenManager.getToken('contextToken')
    if (!contextToken) {
      throw new Error('Context token not found')
    }

    const payload = decodeJWT(contextToken)
    if (!payload) {
      throw new Error('Failed to decode context token')
    }

    const orgSlug = payload.organizationSlug || (payload as any).orgSlug
    if (!orgSlug) {
      throw new Error('Organization slug not found in token')
    }

    return orgSlug
  }

  /**
   * Get organization settings (only organization-scoped, no inherited platform settings)
   */
  async getSettings(params: GetSettingsParams = {}): Promise<Setting[]> {
    const orgSlug = this.getOrgSlug()
    const queryParams = new URLSearchParams()
    
    if (params.category) {
      queryParams.append('category', params.category)
    }
    
    // Only show organization-scoped settings, exclude inherited platform settings
    queryParams.append('includeInherited', 'false')
    
    // Don't group by category - return flat array
    queryParams.append('groupByCategory', 'false')

    const url = `${ORGANIZATION_API_ENDPOINTS.SETTINGS.GET(orgSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    // Handle both flat array and grouped response
    if (Array.isArray(data.settings)) {
      return data.settings
    }
    
    if (data.groupedByCategory) {
      // Flatten grouped response
      return Object.values(data.groupedByCategory).flat() as Setting[]
    }
    
    return []
  }

  /**
   * Get categories for organization settings
   */
  async getCategories(params: GetSettingsParams = {}): Promise<SettingCategory[]> {
    const orgSlug = this.getOrgSlug()
    const queryParams = new URLSearchParams()
    
    // Get grouped response to extract categories
    queryParams.append('groupByCategory', 'true')
    // Only show organization-scoped settings, exclude inherited platform settings
    queryParams.append('includeInherited', 'false')
    
    if (params.category) {
      queryParams.append('category', params.category)
    }

    const url = `${ORGANIZATION_API_ENDPOINTS.SETTINGS.GET(orgSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    if (data.groupedByCategory) {
      return Object.keys(data.groupedByCategory).map((categoryKey) => {
        const categorySettings = data.groupedByCategory[categoryKey] || []
        return {
          name: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          key: categoryKey,
          count: categorySettings.length,
        }
      })
    }
    
    // Fallback: extract categories from flat array
    if (Array.isArray(data.settings)) {
      const uniqueCategories = Array.from(new Set(data.settings.map((s: Setting) => s.category))) as string[]
      return uniqueCategories.map((cat: string) => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/([A-Z])/g, ' $1').trim(),
        key: cat,
        count: data.settings.filter((s: Setting) => s.category === cat).length,
      }))
    }
    
    return []
  }

  /**
   * Bulk update organization settings
   */
  async bulkUpdateSettings(input: BulkUpdateSettingsInput): Promise<Setting[]> {
    const orgSlug = this.getOrgSlug()
    
    // Transform updates to backend format
    const settings = input.updates.map((update) => ({
      id: update.id,
      value: update.value,
    }))

    const response = await api.put(ORGANIZATION_API_ENDPOINTS.SETTINGS.UPDATE(orgSlug), {
      settings,
    })
    
    return response.data?.data?.settings || response.data?.data || []
  }

  /**
   * Get current user's settings with inheritance (org context; no branch).
   */
  async getUserSettings(params: GetSettingsParams & { groupByCategory?: boolean; includeInherited?: boolean } = {}): Promise<Setting[]> {
    const orgSlug = this.getOrgSlug()
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    if (params.groupByCategory !== undefined) queryParams.append('groupByCategory', String(params.groupByCategory))
    if (params.includeInherited === false) queryParams.append('includeInherited', 'false')
    const url = `${ORGANIZATION_API_ENDPOINTS.SETTINGS.USER_SETTINGS.GET(orgSlug)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    const data = response.data?.data || response.data
    if (Array.isArray(data.settings)) return data.settings
    if (data.groupedByCategory) return Object.values(data.groupedByCategory).flat() as Setting[]
    return []
  }

  /**
   * Update current user's settings (org context).
   */
  async updateUserSettings(updates: Array<{ id?: string; key?: string; value: any }>): Promise<Setting[]> {
    const orgSlug = this.getOrgSlug()
    const settings = updates.map((u) => ({ id: u.id, key: u.key, value: u.value }))
    const response = await api.put(ORGANIZATION_API_ENDPOINTS.SETTINGS.USER_SETTINGS.UPDATE(orgSlug), { settings })
    return response.data?.data?.settings || response.data?.data || []
  }
}

export const organizationSettingService = new OrganizationSettingService()
export default organizationSettingService

